import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useBlocker, useParams } from 'react-router';
import {
  REMINDER_TEXT_MAX,
  reminderChainChannelLabels,
  reminderChainOffsetUnitLabels,
  reminderChainTimingLabels,
  reminderPlaceholders,
  type ReminderChainChannel,
  type ReminderChainOffsetUnit,
  type ReminderChainTiming,
} from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import {
  Banner,
  Button,
  Dialog,
  Segmented,
  Select,
  Skeleton,
  TextField,
  Textarea,
  useToast,
} from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { usePermissions } from '../../lib/session';
import { useEventType } from './api';
import {
  useReminderChain,
  useReminderChainMutations,
  type ReminderChain,
  type ReminderChainStep,
} from './calendarApi';
import styles from './ReminderChainPage.module.css';

/** A step of the local draft; `key` identifies it across edits (ids of new steps are null). */
interface DraftStep {
  key: string;
  id: string | null;
  channel: ReminderChainChannel;
  timing: ReminderChainTiming;
  offsetValue: number;
  offsetUnit: ReminderChainOffsetUnit;
  /** The text shown in the editor: the own text or the standard text of the channel. */
  text: string | null;
  customText: boolean;
  fixed: boolean;
  /** The server's sublabel while the step is unchanged. */
  sublabel: string | null;
}

type Mode = 'organization' | 'own';

type EditableChannel = Exclude<ReminderChainChannel, 'confirmation' | 'appointment'>;

const channelOptions: ReadonlyArray<{ value: EditableChannel; label: string }> = (
  ['email', 'whatsapp', 'sms'] as const
).map((value) => ({ value, label: reminderChainChannelLabels[value] }));

const timingOptions = (['at_booking', 'before_appointment', 'after_appointment'] as const).map((value) => ({
  value,
  label: reminderChainTimingLabels[value],
}));

const unitOptions = (['minutes', 'hours', 'days'] as const).map((value) => ({
  value,
  label: reminderChainOffsetUnitLabels[value],
}));

const DESCRIPTION =
  'Bestätigungen und Erinnerungen als Kette. Schritte anklicken zum Bearbeiten, mit + neue Schritte einfügen — auch zwischen bestehenden.';

let keySequence = 0;
function nextKey(): string {
  keySequence += 1;
  return `step-${keySequence}`;
}

function toDraft(chain: ReminderChain): DraftStep[] {
  return chain.steps.map((step: ReminderChainStep) => ({
    key: step.id ?? nextKey(),
    id: step.id,
    channel: step.channel,
    timing: step.timing,
    offsetValue: step.offsetValue,
    offsetUnit: step.offsetUnit,
    text: step.text,
    customText: step.customText,
    fixed: step.fixed,
    sublabel: step.sublabel,
  }));
}

/** „3 Std vorher · eigener Text" for a changed step (the server's wording, A2 of Beratung 07). */
function sublabelOf(step: DraftStep): string {
  if (step.sublabel !== null) return step.sublabel;
  const unit =
    step.offsetUnit === 'days'
      ? step.offsetValue === 1
        ? 'Tag'
        : 'Tage'
      : reminderChainOffsetUnitLabels[step.offsetUnit];
  let text: string;
  switch (step.timing) {
    case 'at_booking':
      text = step.channel === 'confirmation' ? 'bei Buchung · fix, mit ICS' : 'bei Buchung';
      break;
    case 'before_appointment':
      text = `${step.offsetValue} ${unit} vorher`;
      break;
    case 'after_appointment':
      text = `${step.offsetValue} ${unit} danach`;
      break;
    case 'at_start':
      text = 'Beginn';
      break;
  }
  return step.customText ? `${text} · eigener Text` : text;
}

function standardText(chain: ReminderChain, channel: ReminderChainChannel): string {
  switch (channel) {
    case 'confirmation':
      return chain.defaultTexts.confirmation;
    case 'email':
      return chain.defaultTexts.email;
    case 'whatsapp':
      return chain.defaultTexts.whatsapp;
    case 'sms':
      return chain.defaultTexts.sms;
    case 'appointment':
      return '';
  }
}

/** Keeps after-steps behind the anchor and the others in front of it (the server's ordering rule). */
function reorder(steps: DraftStep[], key: string): DraftStep[] {
  const index = steps.findIndex((step) => step.key === key);
  const anchor = steps.findIndex((step) => step.channel === 'appointment');
  const step = steps[index];
  if (!step || anchor === -1) return steps;
  const after = step.timing === 'after_appointment';
  if (after === index > anchor) return steps;
  const rest = steps.filter((entry) => entry.key !== key);
  const anchorInRest = rest.findIndex((entry) => entry.channel === 'appointment');
  const position = after ? anchorInRest + 1 : anchorInRest;
  return [...rest.slice(0, position), step, ...rest.slice(position)];
}

function newStep(chain: ReminderChain, afterAnchor: boolean): DraftStep {
  const channel: EditableChannel = 'email';
  return {
    key: nextKey(),
    id: null,
    channel,
    timing: afterAnchor ? 'after_appointment' : 'before_appointment',
    offsetValue: afterAnchor ? 2 : 24,
    offsetUnit: 'hours',
    text: standardText(chain, channel),
    customText: false,
    fixed: false,
    sublabel: null,
  };
}

function sameSteps(a: DraftStep[], b: DraftStep[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((step, index) => {
    const other = b[index];
    return (
      other !== undefined &&
      step.id === other.id &&
      step.channel === other.channel &&
      step.timing === other.timing &&
      step.offsetValue === other.offsetValue &&
      step.offsetUnit === other.offsetUnit &&
      step.customText === other.customText &&
      (!step.customText || step.text === other.text)
    );
  });
}

/** „Schritt 4: Bitte einen Abstand größer als 0 angeben." from the server's `steps.<i>.<field>` keys. */
function validationLines(error: unknown): string[] {
  if (!(error instanceof ApiError)) return [];
  return Object.entries(error.fields).map(([key, message]) => {
    const match = /^steps\.(\d+)/.exec(key);
    return match ? `Schritt ${Number(match[1]) + 1}: ${message}` : message;
  });
}

interface EditorForm {
  channel: EditableChannel;
  timing: ReminderChainTiming;
  offsetValue: string;
  offsetUnit: ReminderChainOffsetUnit;
  text: string;
  customText: boolean;
}

function formOf(step: DraftStep): EditorForm {
  return {
    channel: step.channel === 'confirmation' || step.channel === 'appointment' ? 'email' : step.channel,
    timing: step.timing,
    offsetValue: String(step.offsetValue),
    offsetUnit: step.offsetUnit,
    text: step.text ?? '',
    customText: step.customText,
  };
}

interface ChainEditorProps {
  eventTypeId: string | null;
  chain: ReminderChain;
  canEdit: boolean;
  backHref: string;
  backLabel: string;
}

function ChainEditor({ eventTypeId, chain, canEdit, backHref, backLabel }: ChainEditorProps) {
  const { toast } = useToast();
  const { save, remove } = useReminderChainMutations(eventTypeId);
  const serverDraft = useMemo(() => toDraft(chain), [chain]);
  const [mode, setMode] = useState<Mode>(chain.mode);
  const [steps, setSteps] = useState<DraftStep[]>(serverDraft);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [form, setForm] = useState<EditorForm | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [offsetError, setOffsetError] = useState<string | null>(null);

  useEffect(() => {
    setMode(chain.mode);
    setSteps(serverDraft);
    const first = serverDraft.find((step) => !step.fixed && step.channel !== 'appointment') ?? null;
    setSelectedKey(first?.key ?? null);
    setForm(first ? formOf(first) : null);
  }, [chain.mode, serverDraft]);

  const typeChain = eventTypeId !== null;
  const editable = canEdit && (!typeChain || mode === 'own');
  const selected = steps.find((step) => step.key === selectedKey) ?? null;
  const dirty = mode !== chain.mode || !sameSteps(steps, serverDraft);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);

  function select(step: DraftStep) {
    if (step.fixed || step.channel === 'appointment') return;
    setSelectedKey(step.key);
    setForm(formOf(step));
    setOffsetError(null);
  }

  function insertAt(position: number) {
    const anchor = steps.findIndex((step) => step.channel === 'appointment');
    const step = newStep(chain, anchor !== -1 && position > anchor);
    setSteps([...steps.slice(0, position), step, ...steps.slice(position)]);
    select(step);
  }

  /** Builds the step from the editor fields; null when the distance is not a number. */
  function applied(): DraftStep | null {
    if (!selected || !form) return null;
    const offset = form.timing === 'at_booking' ? 0 : Number(form.offsetValue);
    if (!Number.isInteger(offset) || offset < 0 || offset > 1000) {
      setOffsetError('Bitte eine ganze Zahl eingeben.');
      return null;
    }
    setOffsetError(null);
    const standard = standardText(chain, form.channel);
    const customText = form.text.trim() !== '' && form.text !== standard;
    const changed =
      selected.channel !== form.channel ||
      selected.timing !== form.timing ||
      selected.offsetValue !== offset ||
      selected.offsetUnit !== form.offsetUnit ||
      selected.customText !== customText ||
      (customText && selected.text !== form.text);
    return {
      ...selected,
      channel: form.channel,
      timing: form.timing,
      offsetValue: offset,
      offsetUnit: form.offsetUnit,
      text: customText ? form.text : standard,
      customText,
      sublabel: changed ? null : selected.sublabel,
    };
  }

  function apply(): DraftStep[] | null {
    const step = applied();
    if (!step) return null;
    const next = reorder(
      steps.map((entry) => (entry.key === step.key ? step : entry)),
      step.key,
    );
    setSteps(next);
    setForm(formOf(step));
    return next;
  }

  function removeSelected() {
    if (!selected) return;
    const index = steps.findIndex((step) => step.key === selected.key);
    const next = steps.filter((step) => step.key !== selected.key);
    setSteps(next);
    const neighbour =
      next.slice(index).find((step) => !step.fixed && step.channel !== 'appointment') ??
      [...next.slice(0, index)].reverse().find((step) => !step.fixed && step.channel !== 'appointment') ??
      null;
    setSelectedKey(neighbour?.key ?? null);
    setForm(neighbour ? formOf(neighbour) : null);
  }

  async function saveChain() {
    // The open editor counts as applied (the user expects „Ablauf speichern" to keep what they see).
    const current = form && selected ? apply() : steps;
    if (!current) return;
    try {
      await save.mutateAsync({
        steps: current.map((step) => ({
          ...(step.id ? { id: step.id } : {}),
          channel: step.channel,
          timing: step.timing,
          offsetValue: step.offsetValue,
          offsetUnit: step.offsetUnit,
          text: step.fixed || step.channel === 'appointment' || !step.customText ? null : step.text,
        })),
      });
      toast({ message: 'Ablauf gespeichert.' });
    } catch {
      // The banner under the chain shows the server's messages.
    }
  }

  async function inherit() {
    try {
      await remove.mutateAsync();
      setRemoveOpen(false);
      toast({ message: 'Der Ereignistyp nutzt wieder den Org-Standard.' });
    } catch {
      setRemoveOpen(false);
    }
  }

  function changeMode(next: Mode) {
    if (next === mode) return;
    if (next === 'organization' && chain.mode === 'own') {
      setRemoveOpen(true);
      return;
    }
    setMode(next);
    if (next === 'organization') setSteps(serverDraft);
  }

  const lines = validationLines(save.error);
  const saveError = save.isError && lines.length === 0 ? errorMessage(save.error) : null;

  return (
    <div className={styles.page}>
      <Link to={backHref} className={styles.crumb}>
        ← {backLabel}
      </Link>
      <div className={styles.head}>
        <div>
          <h1 className={styles.title}>Ablauf</h1>
          <p className={styles.description}>{DESCRIPTION}</p>
        </div>
        {typeChain ? (
          <Segmented
            label="Ablauf"
            variant="accent"
            className={styles.segmented}
            items={[
              { value: 'organization', label: 'Org-Standard', disabled: !canEdit },
              { value: 'own', label: 'Nur dieser Ereignistyp', disabled: !canEdit },
            ]}
            value={mode}
            onChange={changeMode}
          />
        ) : null}
      </div>

      {!canEdit ? (
        <Banner tone="neutral">
          {typeChain
            ? 'Diesen Ereignistyp bearbeiten Admins oder der Besitzer.'
            : 'Den Org-Standard bearbeiten Admins.'}
        </Banner>
      ) : null}

      <section className={styles.card} aria-label="Kette">
        <div className={styles.chain}>
          {steps.map((step, index) => {
            const anchor = step.channel === 'appointment';
            const selectable = editable && !step.fixed && !anchor;
            const tileClass = clsx(
              styles.tile,
              step.channel === 'confirmation' && styles.tileFixed,
              anchor && styles.tileAnchor,
              step.key === selectedKey && editable && styles.tileSelected,
            );
            const content = (
              <>
                <span className={styles.tileLabel}>{reminderChainChannelLabels[step.channel]}</span>
                <span className={styles.tileSub}>{sublabelOf(step)}</span>
              </>
            );
            return (
              <Fragment key={step.key}>
                {selectable ? (
                  <button
                    type="button"
                    className={tileClass}
                    aria-pressed={step.key === selectedKey}
                    onClick={() => select(step)}
                  >
                    {content}
                  </button>
                ) : (
                  <div className={tileClass}>{content}</div>
                )}
                <span className={styles.connector} aria-hidden="true" />
                {editable ? (
                  <button
                    type="button"
                    className={styles.insert}
                    aria-label={`Schritt nach „${reminderChainChannelLabels[step.channel]}" einfügen`}
                    onClick={() => insertAt(index + 1)}
                  >
                    +
                  </button>
                ) : (
                  <span className={clsx(styles.insert, styles.insertInert)} aria-hidden="true">
                    +
                  </span>
                )}
                {index < steps.length - 1 ? <span className={styles.connector} aria-hidden="true" /> : null}
              </Fragment>
            );
          })}
        </div>

        {lines.length > 0 ? (
          <Banner tone="danger" className={styles.errors}>
            <ul className={styles.errorList}>
              {lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Banner>
        ) : null}
        {saveError ? (
          <Banner tone="danger" className={styles.errors}>
            {saveError}
          </Banner>
        ) : null}
        {remove.isError ? (
          <Banner tone="danger" className={styles.errors}>
            {errorMessage(remove.error)}
          </Banner>
        ) : null}

        <div className={styles.editor}>
          {editable && selected && form ? (
            <>
              <div className={styles.fields}>
                <Select
                  label="Kanal"
                  options={channelOptions}
                  value={form.channel}
                  onChange={(event) => {
                    const channel = event.target.value as EditableChannel;
                    setForm({
                      ...form,
                      channel,
                      text: form.customText ? form.text : standardText(chain, channel),
                    });
                  }}
                />
                <Select
                  label="Zeitpunkt"
                  options={timingOptions}
                  value={form.timing}
                  onChange={(event) =>
                    setForm({ ...form, timing: event.target.value as ReminderChainTiming })
                  }
                />
                <div className={styles.distance}>
                  <span className={styles.distanceLabel} id="reminder-distance">
                    Abstand
                  </span>
                  <div className={styles.distanceRow}>
                    <TextField
                      label="Abstand"
                      labelHidden
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={1000}
                      className={styles.distanceValue}
                      value={form.timing === 'at_booking' ? '0' : form.offsetValue}
                      disabled={form.timing === 'at_booking'}
                      error={offsetError ?? undefined}
                      onChange={(event) => setForm({ ...form, offsetValue: event.target.value })}
                    />
                    <Select
                      label="Einheit"
                      labelHidden
                      options={unitOptions}
                      className={styles.distanceUnit}
                      value={form.offsetUnit}
                      disabled={form.timing === 'at_booking'}
                      onChange={(event) =>
                        setForm({ ...form, offsetUnit: event.target.value as ReminderChainOffsetUnit })
                      }
                    />
                  </div>
                </div>
              </div>
              <div className={styles.textLabel} aria-hidden="true">
                <span className={styles.textLabelName}>Nachrichtentext</span>
                {form.customText ? (
                  <span className={styles.ownText}>· eigener Text für diesen Schritt</span>
                ) : (
                  <span className={styles.standardText}>· Standardtext</span>
                )}
              </div>
              <Textarea
                label="Nachrichtentext"
                labelHidden
                className={styles.text}
                rows={2}
                autoGrow
                maxLength={REMINDER_TEXT_MAX}
                value={form.text}
                onChange={(event) => {
                  const text = event.target.value;
                  setForm({ ...form, text, customText: text !== standardText(chain, form.channel) });
                }}
                hint={`Variablen werden beim Versand ersetzt: ${reminderPlaceholders.map((name) => `{{${name}}}`).join(' ')}`}
              />
            </>
          ) : (
            <p className={styles.editorHint}>
              {!editable && typeChain && canEdit
                ? 'Dieser Ereignistyp nutzt den Org-Standard. Mit „Nur dieser Ereignistyp" legen Sie einen eigenen Ablauf an.'
                : editable
                  ? 'Wählen Sie einen Schritt aus oder fügen Sie mit + einen neuen ein.'
                  : null}
            </p>
          )}
          {canEdit ? (
            <div className={styles.actions}>
              {editable && selected && form ? (
                <>
                  <Button variant="secondary" className={styles.actionButton} onClick={() => void apply()}>
                    Übernehmen
                  </Button>
                  <Button
                    variant="destructive"
                    className={clsx(styles.actionButton, styles.removeButton)}
                    onClick={removeSelected}
                  >
                    Schritt entfernen
                  </Button>
                  <button
                    type="button"
                    className={styles.textLink}
                    onClick={() =>
                      setForm({ ...form, text: standardText(chain, form.channel), customText: false })
                    }
                  >
                    Zurück zum Standardtext
                  </button>
                </>
              ) : null}
              <span className={styles.actionsEnd}>
                <Button
                  className={styles.actionButton}
                  loading={save.isPending}
                  disabled={!editable}
                  onClick={() => void saveChain()}
                >
                  Ablauf speichern
                </Button>
                {typeChain && chain.mode === 'own' ? (
                  <button type="button" className={styles.inheritLink} onClick={() => setRemoveOpen(true)}>
                    Eigenen Ablauf entfernen — Org-Standard erben
                  </button>
                ) : null}
              </span>
            </div>
          ) : null}
        </div>
      </section>

      <Dialog
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        eyebrow="Ablauf"
        title="Eigenen Ablauf entfernen?"
        width={560}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemoveOpen(false)}>
              Abbrechen
            </Button>
            <Button variant="destructiveFilled" loading={remove.isPending} onClick={() => void inherit()}>
              Entfernen
            </Button>
          </>
        }
      >
        <p className={styles.dialogText}>
          Der Ereignistyp erbt danach wieder den Org-Standard. Bereits geplante Erinnerungen bleiben bestehen.
        </p>
      </Dialog>

      <Dialog
        open={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        eyebrow="Ablauf"
        title="Änderungen verwerfen?"
        width={560}
        footer={
          <>
            <Button variant="secondary" onClick={() => blocker.reset?.()}>
              Weiter bearbeiten
            </Button>
            <Button variant="destructiveFilled" onClick={() => blocker.proceed?.()}>
              Verwerfen
            </Button>
          </>
        }
      >
        <p className={styles.dialogText}>Der Ablauf ist noch nicht gespeichert.</p>
      </Dialog>
    </div>
  );
}

function ChainSkeleton() {
  return (
    <div className={styles.page} aria-busy="true" aria-label="Ablauf wird geladen">
      <Skeleton width={160} height={14} />
      <Skeleton width="40%" height={28} />
      <Skeleton height={330} />
    </div>
  );
}

/** The chain of one event type (Beratung 07, `/calendar/event-types/:id/reminders`). */
function EventTypeChain({ id }: { id: string }) {
  const type = useEventType(id);
  const chain = useReminderChain(id);
  if (type.isError || chain.isError) {
    return (
      <div className={styles.page}>
        <Link to="/calendar/event-types" className={styles.crumb}>
          ← Ereignistypen
        </Link>
        <Banner tone="danger">{errorMessage(type.error ?? chain.error)}</Banner>
      </div>
    );
  }
  if (!type.data || !chain.data) return <ChainSkeleton />;
  return (
    <ChainEditor
      eventTypeId={id}
      chain={chain.data}
      canEdit={type.data.canEdit}
      backHref={`/calendar/event-types/${id}`}
      backLabel={type.data.name}
    />
  );
}

/** The organization default (`/calendar/reminders`, admins edit). */
function DefaultChain() {
  const permissions = usePermissions();
  const chain = useReminderChain(null);
  if (chain.isError) {
    return (
      <div className={styles.page}>
        <Link to="/calendar/event-types" className={styles.crumb}>
          ← Ereignistypen
        </Link>
        <Banner tone="danger">{errorMessage(chain.error)}</Banner>
      </div>
    );
  }
  if (!chain.data) return <ChainSkeleton />;
  return (
    <ChainEditor
      eventTypeId={null}
      chain={chain.data}
      canEdit={permissions.is('settings.organization', 'yes')}
      backHref="/calendar/event-types"
      backLabel="Ereignistypen"
    />
  );
}

/** Beratung 07 — „Ablauf": the reminder chain of an event type or of the organization. */
export function ReminderChainPage() {
  const { id } = useParams();
  return id ? <EventTypeChain id={id} /> : <DefaultChain />;
}
